import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { Radio, BookOpen, ArrowRight } from 'lucide-react';
import PageHeader from '../components/PageHeader';

// Dispatch-Live-Ansicht (§ 49 Abs. 4 PBefG): Aufträge erscheinen hier in dem Moment, in dem sie
// am Betriebssitz EINGEHEN, und verschwinden nach dem Betriebssitz-Durchlauf (dispatch_hold_sec),
// wenn sie an den Fahrer weitergeleitet werden. Rein lesend — ändert nichts am Matching.
// Der Server liefert remaining_sec (kein Uhren-Versatz); zwischen den Polls tickt die Anzeige lokal.

const TL = { standard: 'VOOGO Basis', comfort: 'VOOGO Komfort', premium: 'VOOGO Business', xl: 'VOOGO Family', max: 'VOOGO Van', taxi: 'VOOGO Taxi' };   // 'VOOGO X' war der KONKURRENZ-Name (Branding-Falle)!

export default function Dispatch() {
  const [holdSec, setHoldSec] = useState(10);
  const [entries, setEntries] = useState([]);   // [{ ...entry, expiresAtMs }]
  const [now, setNow] = useState(Date.now());
  const [loaded, setLoaded] = useState(false);
  const seenRef = useRef(0);                    // Zähler „heute gesehen" (nur Sitzung)
  const knownRef = useRef(new Set());
  const navigate = useNavigate();

  // Polling: alle 2 s frische Liste; expiresAt aus server-seitigem remaining_sec ableiten.
  useEffect(() => {
    let alive = true;
    const load = () => {
      api.get('/partners/dispatch').then(r => {
        if (!alive) return;
        const t = Date.now();
        setHoldSec(r.data.hold_sec ?? 10);
        const list = (r.data.entries || []).map(e => ({ ...e, expiresAtMs: t + (e.remaining_sec || 0) * 1000 }));
        for (const e of list) {
          if (!knownRef.current.has(e.ride_id)) { knownRef.current.add(e.ride_id); seenRef.current += 1; }
        }
        setEntries(list);
        setLoaded(true);
      }).catch(() => {});
    };
    load();
    const iv = setInterval(load, 2000);
    return () => { alive = false; clearInterval(iv); };
  }, []);

  // Lokaler Ticker für flüssigen Countdown/Balken zwischen den Polls.
  useEffect(() => {
    const iv = setInterval(() => setNow(Date.now()), 200);
    return () => clearInterval(iv);
  }, []);

  const visible = entries.filter(e => e.expiresAtMs > now);

  return (
    <div>
      <PageHeader
        title="Dispatch"
        actions={
          <button onClick={() => navigate('/order-log')}
            style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '9px 16px', background: '#f3f4f6', borderRadius: 20, fontSize: 13, fontWeight: 700, border: 'none', cursor: 'pointer' }}>
            <BookOpen size={15} /> Auftragsbuch
          </button>
        }
      />
      <div style={{ fontSize: 13, color: '#4b5563', marginBottom: 22, display: 'flex', alignItems: 'center', gap: 6 }}>
        <Radio size={14} /> Eingehende Aufträge am Betriebssitz (§ 49 Abs. 4 PBefG) — sichtbar für {holdSec} Sekunden, dann Weiterleitung an den Fahrer
      </div>

      {visible.length === 0 ? (
        <div style={{ border: '1px dashed #d1d5db', borderRadius: 16, padding: '60px 24px', textAlign: 'center' }}>
          <span style={{ position: 'relative', display: 'inline-flex', width: 14, height: 14, marginBottom: 14 }}>
            <span style={{ position: 'absolute', inset: 0, borderRadius: '50%', background: '#9C27B0', opacity: 0.3, animation: 'vgPing 1.6s ease-out infinite' }} />
            <span style={{ position: 'absolute', inset: 3, borderRadius: '50%', background: '#9C27B0' }} />
          </span>
          <div style={{ fontSize: 16, fontWeight: 800 }}>{loaded ? 'Warte auf eingehende Aufträge …' : 'Lade …'}</div>
          <div style={{ fontSize: 13, color: '#4b5563', marginTop: 6, lineHeight: 1.5 }}>
            Neue Aufträge an deine Fahrer erscheinen hier live für {holdSec} Sekunden,<br />
            bevor sie weitergeleitet werden. Die dauerhafte Dokumentation steht im Auftragsbuch.
          </div>
          {seenRef.current > 0 && <div style={{ fontSize: 12, color: '#6b7280', marginTop: 12 }}>{seenRef.current} Aufträge in dieser Sitzung gesehen</div>}
          <style>{'@keyframes vgPing { 0% { transform: scale(1); opacity: .5 } 80%, 100% { transform: scale(2.6); opacity: 0 } }'}</style>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 12 }}>
          {visible.map(e => {
            const remaining = Math.max(0, (e.expiresAtMs - now) / 1000);
            const frac = holdSec > 0 ? Math.min(1, remaining / holdSec) : 0;
            return (
              <div key={e.ride_id} style={{ border: '1px solid #e5e7eb', borderRadius: 16, padding: '16px 18px', position: 'relative', overflow: 'hidden' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 14, flexWrap: 'wrap' }}>
                  <div style={{ minWidth: 240, flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                      <span style={{ width: 9, height: 9, borderRadius: '50%', background: '#9C27B0' }} />
                      <span style={{ fontSize: 12, fontWeight: 800, color: '#9C27B0', textTransform: 'uppercase', letterSpacing: 0.6 }}>Eingehender Auftrag</span>
                      {e.ride_type && <span style={{ fontSize: 12, fontWeight: 700, color: '#4b5563', background: '#f3f4f6', padding: '2px 9px', borderRadius: 20 }}>{TL[e.ride_type] || e.ride_type}</span>}
                    </div>
                    <div style={{ fontSize: 16, fontWeight: 800, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.pickup_address || '—'}</div>
                    <div style={{ fontSize: 14, color: '#4b5563', display: 'flex', alignItems: 'center', gap: 6, marginTop: 2 }}>
                      <ArrowRight size={13} style={{ flexShrink: 0 }} />
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.dropoff_address || '—'}</span>
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: 13, fontWeight: 700 }}>{e.driver_name || 'Fahrer'}</div>
                    <div style={{ fontSize: 12, color: '#4b5563' }}>
                      {[e.vehicle_brand, e.vehicle_model].filter(Boolean).join(' ')}{e.license_plate ? ` · ${e.license_plate}` : ''}
                    </div>
                    <div style={{ fontSize: 12, color: '#9C27B0', fontWeight: 800, marginTop: 6 }}>
                      Weiterleitung in {Math.ceil(remaining)} s
                    </div>
                  </div>
                </div>
                {/* Countdown-Balken: läuft von voll auf leer über den Durchlauf */}
                <div style={{ position: 'absolute', left: 0, bottom: 0, height: 4, width: `${frac * 100}%`, background: '#9C27B0', transition: 'width 0.2s linear' }} />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
